import { useLocalStorage } from '@mantine/hooks'
import { Alert, Button, IconButton } from '@mui/material'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import {
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { match } from 'ts-pattern'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineNotifications from '~icons/ic/baseline-notifications'
import IcBaselineSettings from '~icons/ic/baseline-settings'
import { NotificationType } from 'src/services/apollo/generated/graphql'
import { executeMarkNotificationRead } from 'src/services/apollo/notification'
import { isNotificationAvailable, isServiceWorkerAvailable } from 'src/utils'
import { refreshPushToken } from 'src/utils/swClient'
import { track } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { UITab, UITabList } from '../ui/Tab'
import type { TabType } from './common'
import { useNotificationTabs } from './common'
import NotificationList from './NotificationList'
import { NotificationListContext } from './NotificationListContext'
import { useUnreadNotificationNews } from './NotificationNews/NotificationNewsButton'
import NotificationNewsContent from './NotificationNews/NotificationNewsContent'

const NotificationPanel = (props: { onTabChange?: () => void }) => {
  const { t } = useTranslation(['common'])
  const [tabValue, setTabValue] = useState<TabType>('likes')
  const [panelWidth, setPanelWidth] = useState<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const tabValues = useNotificationTabs()

  const [showPermissionPrompt, setShowPermissionPrompt] = useState(true)
  const [dismissedPermissionPrompt, setDismissedPermissionPrompt] =
    useLocalStorage({
      key: 'notification::dismissed-inbox-permission-prompt',
      defaultValue: false,
    })
  const permissionNotGranted =
    isNotificationAvailable && Notification.permission !== 'granted'
  const promptPermission =
    isServiceWorkerAvailable && // Do not prompt for notification unless sw available
    permissionNotGranted &&
    showPermissionPrompt &&
    !dismissedPermissionPrompt

  const handleChange = (newValue: string) => {
    track('notification_tab_click', {
      tab: newValue,
    })
    setTabValue(newValue as TabType)
    props.onTabChange?.()
  }
  const [, markTypeAsRead] = useLoadingCallback(
    async (types: NotificationType[]) => {
      if (types.length < 1) return
      try {
        await executeMarkNotificationRead({ types })
      } catch (e: any) {
        toast.error(`${t('common:notification.maar.failed')}: ${e.message}`)
      }
    },
  )

  useEffect(() => {
    setPanelWidth(containerRef.current?.clientWidth ?? 0)
    // Recalculate on tab changes
  }, [tabValue])

  return (
    <div
      className="max-w-full p-2 w-min min-w-[340px]"
      ref={containerRef}
      style={
        {
          '--panel-width': `${panelWidth}px`,
        } as CSSProperties
      }
    >
      <div className="relative w-full flex items-center justify-center p-3">
        <h1 className="font-bold">{t('common:updates')}</h1>

        <IconButton
          className="absolute right-1"
          href="/profile/edit/notification"
          onClick={() => {
            track('setting_push_click')
          }}
        >
          <IcBaselineSettings className="size-[1em]" />
        </IconButton>
      </div>

      <AriaTabs
        selectedKey={tabValue}
        onSelectionChange={val => handleChange(val as string)}
        orientation="horizontal"
      >
        {/* takes as much space as possible on larger screens */}
        <div className="w-full sm:w-max px-0 md:px-8 border-b border-background-light">
          <UITabList variant="underlined" className="w-full">
            {tabValues.map(v => (
              <UITab
                variant="underlined"
                color="theme"
                className="normal-case text-sm px-2 min-w-0 md:min-w-[90px] md:px-4 md:text-base"
                key={v.label}
                id={v.label}
              >
                <p className="flex items-center justify-center">
                  {t(`common:notification.type.${v.label}`)}
                  <BadgeWrapper types={v.types} />
                </p>
              </UITab>
            ))}
          </UITabList>
        </div>

        {tabValues.map(v => (
          <AriaTabPanel
            id={v.label}
            key={v.label}
            className="max-h-[min(400px,calc(100vh-160px))] md:max-h-[800px] overflow-y-auto w-full py-1 px-2"
          >
            {promptPermission && (
              <Alert
                className="flex-wrap"
                icon={
                  <IcBaselineNotifications
                    className="size-[1em]"
                    style={{ fontSize: 'inherit' }}
                  />
                }
                severity="info"
                action={
                  <>
                    <Button
                      className="font-semibold"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        refreshPushToken(true, 'inbox_banner')
                        setShowPermissionPrompt(false)
                      }}
                    >
                      {t('common:notification.permission-banner.action')}
                    </Button>
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => {
                        track('notification_permission_dismiss_click', {
                          scene: 'inbox_banner',
                        })
                        setDismissedPermissionPrompt(true)
                        setShowPermissionPrompt(false)
                      }}
                    >
                      <IcBaselineClose className="size-[1em] text-lg" />
                    </IconButton>
                  </>
                }
              >
                {t('common:notification.permission-banner.label')}
              </Alert>
            )}

            {match(v)
              .with({ listType: 'plain' }, () => (
                <NotificationList
                  id={v.id}
                  types={v.types}
                  onMarkRead={markTypeAsRead}
                />
              ))
              .when(
                v =>
                  v.listType === 'external' &&
                  v.types[0] === NotificationType.News,
                () => <NotificationNewsTabContent />,
              )
              .otherwise(() => null)}
          </AriaTabPanel>
        ))}
      </AriaTabs>
    </div>
  )
}

function NotificationNewsTabContent() {
  const { updateLastRead } = useUnreadNotificationNews()

  useEffect(() => {
    // read all news on panel rendering
    updateLastRead()
  }, [])

  return (
    // panel width is needed for news content, since news item utilizes line-clamp
    // otherwise news content will overflow out of the panel
    <div className="w-[calc(var(--panel-width)-1.5rem)] pt-2">
      <NotificationNewsContent />
    </div>
  )
}

const BadgeWrapper = (props: { types: NotificationType[] }) => {
  const { types } = props
  const { unreadCounts } = useContext(NotificationListContext)

  const curTabUnreadCount = unreadCounts
    .filter(c => c.type && types.includes(c.type))
    .reduce((acc, c) => acc + (c.count ?? 0), 0)

  if (types[0] === NotificationType.News) return <NewsBadgeWrapper />

  if (curTabUnreadCount < 1) return null

  return (
    <span className="inline-flex bg-purple-600 text-white h-5 min-w-[20px] py-0.5 px-1.5 items-center justify-center rounded-full ml-[6px] text-xs">
      {curTabUnreadCount}
    </span>
  )
}

const NewsBadgeWrapper = () => {
  const { hasUnread } = useUnreadNotificationNews()

  if (!hasUnread) return null

  return (
    <span className="bg-purple-600 size-[8px] rounded-full ml-[6px]"></span>
  )
}

export default NotificationPanel
